import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState, type FormEvent } from "react";
import {
  ArrowRight,
  CircleCheck,
  FileText,
  Flag,
  Headset,
  LifeBuoy,
  Pencil,
  Plus,
  Trash2,
} from "lucide-react";
import { toast } from "sonner";
import PageHeader from "@/components/shared/PageHeader";
import SectionCard from "@/components/shared/SectionCard";
import StatCard from "@/components/shared/StatCard";
import EmptyState from "@/components/shared/EmptyState";
import ConfirmationDialog from "@/components/shared/ConfirmationDialog";
import { IconAction } from "@/components/shared/IconAction";
import { SearchInput } from "@/components/shared/SearchInput";
import { ListSkeleton, StatCardsSkeleton, TableSkeleton } from "@/components/shared/Skeletons";
import { TicketConversation, formatDateTime } from "@/components/support/TicketConversation";
import { Field } from "@/components/forms";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import {
  TICKET_PRIORITY,
  TICKET_STATUS,
  categoryLabel,
  useAdminFaqs,
  useAdminTicket,
  useAdminTickets,
  useDeleteFaq,
  useSaveFaq,
  useStaffReply,
  useSupportStats,
  useUpdateTicket,
  type FaqAudience,
  type SupportFaq,
  type TicketPriority,
  type TicketStatus,
} from "@/hooks/useSupport";
import { formatDate } from "@/utils/formatters";

export const Route = createFileRoute("/admin/help-support/")({
  component: AdminHelpSupportPage,
});

const AUDIENCE_LABEL: Record<FaqAudience, string> = {
  all: "Everyone",
  student: "Students",
  partner: "Partners",
};

function AdminHelpSupportPage() {
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [openTicketId, setOpenTicketId] = useState<number | null>(null);
  const [kbOpen, setKbOpen] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  const statsQuery = useSupportStats();
  const ticketsQuery = useAdminTickets(statusFilter, debouncedSearch);
  const faqsQuery = useAdminFaqs();
  const tickets = ticketsQuery.data ?? [];
  const publishedFaqs = (faqsQuery.data ?? []).filter((f) => f.published);
  const stats = statsQuery.data;

  return (
    <div className="space-y-6 pb-4">
      <PageHeader
        title="Support coordination"
        subtitle="Answer support tickets from students and partners, and maintain the knowledge base they see on their Help & support page."
        breadcrumbs={[{ label: "Dashboard", path: "/admin/dashboard" }, { label: "Support" }]}
      />

      {statsQuery.isLoading ? (
        <StatCardsSkeleton count={3} />
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3 md:gap-4">
          <StatCard label="Awaiting response" value={stats?.open ?? 0} icon={<LifeBuoy />} />
          <StatCard label="In progress" value={stats?.in_progress ?? 0} icon={<Headset />} />
          <StatCard
            label="Resolved today"
            value={stats?.resolved_today ?? 0}
            icon={<CircleCheck />}
          />
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 md:gap-6 lg:grid-cols-[1.4fr_1fr]">
        <SectionCard
          title="Support tickets"
          className="h-auto self-start"
          actions={
            <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto">
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="h-8 w-[130px]" aria-label="Status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="open">Open</SelectItem>
                  <SelectItem value="in_progress">In progress</SelectItem>
                  <SelectItem value="resolved">Resolved</SelectItem>
                </SelectContent>
              </Select>
              <SearchInput
                value={search}
                onChange={setSearch}
                placeholder="Search tickets…"
                className="min-w-0 flex-1 sm:w-52 sm:flex-none"
              />
            </div>
          }
          noPadding
        >
          {ticketsQuery.isLoading ? (
            <TableSkeleton rows={4} columns={4} />
          ) : ticketsQuery.isError ? (
            <EmptyState
              bare
              icon={<LifeBuoy />}
              title="Couldn't load tickets"
              description="The support queue could not be fetched."
              actionLabel="Retry"
              onAction={() => ticketsQuery.refetch()}
            />
          ) : tickets.length === 0 ? (
            <EmptyState
              bare
              icon={<LifeBuoy />}
              title="No tickets"
              description={
                search || statusFilter !== "all"
                  ? "No tickets match the current search and status filter."
                  : "Tickets students and partners raise from their Help & support page appear here."
              }
            />
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead className="w-12 pl-4 md:pl-5">#</TableHead>
                  <TableHead>Subject</TableHead>
                  <TableHead>Priority</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="pr-4 text-right md:pr-5">
                    <span className="sr-only">Actions</span>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {tickets.map((ticket) => (
                  <TableRow key={ticket.id} className="h-11 [&>td]:py-1.5">
                    <TableCell className="pl-4 font-mono text-caption text-muted-foreground md:pl-5">
                      {ticket.id}
                    </TableCell>
                    <TableCell className="min-w-[220px]">
                      <p className="font-medium text-foreground">{ticket.subject}</p>
                      <p className="text-caption text-muted-foreground">
                        {ticket.user_name || ticket.user_email || "Unknown user"}
                        {ticket.user_role && (
                          <span className="capitalize"> · {ticket.user_role}</span>
                        )}{" "}
                        · <span className="tabular-nums">{formatDate(ticket.created_at)}</span>
                      </p>
                    </TableCell>
                    <TableCell>
                      <Badge variant={TICKET_PRIORITY[ticket.priority]?.variant ?? "outline"}>
                        <Flag />
                        {TICKET_PRIORITY[ticket.priority]?.label ?? ticket.priority}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant={TICKET_STATUS[ticket.status]?.variant ?? "outline"} dot>
                        {TICKET_STATUS[ticket.status]?.label ?? ticket.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="pr-4 text-right md:pr-5">
                      <IconAction
                        label="Manage ticket"
                        icon={<ArrowRight />}
                        onClick={() => setOpenTicketId(ticket.id)}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </SectionCard>

        <SectionCard
          title="Knowledge base"
          description="Published answers students and partners see on their Help & support page."
          className="h-auto self-start"
        >
          {faqsQuery.isLoading ? (
            <ListSkeleton items={3} />
          ) : publishedFaqs.length === 0 ? (
            <p className="text-sm text-muted-foreground">Nothing is published yet.</p>
          ) : (
            <Accordion type="single" collapsible className="-mt-2">
              {publishedFaqs.map((faq) => (
                <AccordionItem key={faq.id} value={`faq-${faq.id}`} className="border-border">
                  <AccordionTrigger className="gap-3 text-left text-sm font-medium hover:no-underline">
                    <span className="flex items-center gap-2.5">
                      <FileText className="size-4 shrink-0 text-muted-foreground" aria-hidden />
                      {faq.question}
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="pl-[26px] leading-6 whitespace-pre-wrap text-body">
                    {faq.answer}
                    {faq.audience !== "all" && (
                      <p className="mt-2 text-caption text-muted-foreground">
                        Shown to {AUDIENCE_LABEL[faq.audience].toLowerCase()} only
                      </p>
                    )}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          )}
          <Button variant="outline" className="mt-4 w-full" onClick={() => setKbOpen(true)}>
            Edit knowledge base
          </Button>
        </SectionCard>
      </div>

      <TicketManageSheet ticketId={openTicketId} onClose={() => setOpenTicketId(null)} />
      <KnowledgeBaseSheet
        open={kbOpen}
        onClose={() => setKbOpen(false)}
        faqs={faqsQuery.data ?? []}
      />
    </div>
  );
}

function TicketManageSheet({
  ticketId,
  onClose,
}: {
  ticketId: number | null;
  onClose: () => void;
}) {
  const { data: ticket, isLoading } = useAdminTicket(ticketId);
  const update = useUpdateTicket(ticketId);
  const reply = useStaffReply(ticketId);

  const change = async (input: { status?: TicketStatus; priority?: TicketPriority }) => {
    try {
      await update.mutateAsync(input);
      toast.success("Ticket updated");
    } catch {
      // Shown by the API client.
    }
  };

  return (
    <Sheet open={ticketId !== null} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="flex w-full flex-col gap-0 p-0 sm:max-w-[560px]">
        <SheetHeader className="space-y-1 border-b border-border px-6 py-5 text-left">
          <p className="eyebrow">Ticket #{ticketId}</p>
          <SheetTitle className="pr-6 text-xl font-normal">
            {isLoading || !ticket ? "Loading…" : ticket.subject}
          </SheetTitle>
          <SheetDescription>
            {ticket
              ? `${ticket.user_name || "Unknown user"}${ticket.user_email ? ` · ${ticket.user_email}` : ""}`
              : ""}
          </SheetDescription>
          {ticket && (
            <div className="grid grid-cols-2 gap-3 pt-3">
              <Field label="Status">
                {({ id }) => (
                  <Select
                    value={ticket.status}
                    onValueChange={(v) => change({ status: v as TicketStatus })}
                    disabled={update.isPending}
                  >
                    <SelectTrigger id={id}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="open">Open</SelectItem>
                      <SelectItem value="in_progress">In progress</SelectItem>
                      <SelectItem value="resolved">Resolved</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              </Field>
              <Field label="Priority">
                {({ id }) => (
                  <Select
                    value={ticket.priority}
                    onValueChange={(v) => change({ priority: v as TicketPriority })}
                    disabled={update.isPending}
                  >
                    <SelectTrigger id={id}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="high">High</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="low">Low</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              </Field>
              <p className="col-span-2 text-caption text-muted-foreground">
                {categoryLabel(ticket.category)}
                {ticket.user_role && <span className="capitalize"> · {ticket.user_role}</span>} ·
                Opened <span className="tabular-nums">{formatDateTime(ticket.created_at)}</span>
              </p>
            </div>
          )}
        </SheetHeader>
        <div className="flex-1 overflow-y-auto px-6 py-5">
          {isLoading || !ticket ? (
            <div className="space-y-3">
              <Skeleton className="h-16 w-full" />
              <Skeleton className="h-16 w-full" />
            </div>
          ) : (
            <TicketConversation
              ticket={ticket}
              viewer="staff"
              onReply={(body) => reply.mutateAsync(body)}
              replying={reply.isPending}
              replyHint={
                ticket.status === "open"
                  ? "Replying moves the ticket to In progress and notifies the requester."
                  : "The requester is notified of your reply."
              }
            />
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

const EMPTY_FAQ = {
  question: "",
  answer: "",
  audience: "all" as FaqAudience,
  sort_order: 0,
  published: true,
};

function KnowledgeBaseSheet({
  open,
  onClose,
  faqs,
}: {
  open: boolean;
  onClose: () => void;
  faqs: SupportFaq[];
}) {
  const [editing, setEditing] = useState<(typeof EMPTY_FAQ & { id?: number }) | null>(null);
  const [deleting, setDeleting] = useState<SupportFaq | null>(null);
  const deleteFaq = useDeleteFaq();

  const confirmDelete = async () => {
    if (!deleting) return;
    try {
      await deleteFaq.mutateAsync(deleting.id);
      toast.success("Entry deleted");
      setDeleting(null);
    } catch {
      // Shown by the API client.
    }
  };

  const nextSortOrder = faqs.reduce((max, f) => Math.max(max, f.sort_order), 0) + 1;

  return (
    <>
      <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
        <SheetContent side="right" className="flex w-full flex-col gap-0 p-0 sm:max-w-[520px]">
          <SheetHeader className="space-y-1 border-b border-border px-6 py-5 text-left">
            <SheetTitle className="text-xl font-normal">Knowledge base</SheetTitle>
            <SheetDescription>
              Questions and answers shown on the Help & support page. Drafts stay hidden until
              published.
            </SheetDescription>
            <div className="pt-2">
              <Button
                size="sm"
                onClick={() => setEditing({ ...EMPTY_FAQ, sort_order: nextSortOrder })}
              >
                <Plus />
                Add question
              </Button>
            </div>
          </SheetHeader>
          <div className="flex-1 overflow-y-auto">
            {faqs.length === 0 ? (
              <EmptyState
                bare
                icon={<FileText />}
                title="No entries"
                description="Add the questions people ask most so they can find answers without opening a ticket."
              />
            ) : (
              <ul className="divide-y divide-border">
                {faqs.map((faq) => (
                  <li key={faq.id} className="flex items-start gap-3 px-6 py-3.5">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-foreground">{faq.question}</p>
                      <p className="mt-0.5 line-clamp-2 text-caption text-muted-foreground">
                        {faq.answer}
                      </p>
                      <div className="mt-1.5 flex flex-wrap gap-1.5">
                        <Badge variant="outline">
                          {AUDIENCE_LABEL[faq.audience] ?? faq.audience}
                        </Badge>
                        {!faq.published && <Badge variant="muted">Draft</Badge>}
                      </div>
                    </div>
                    <div className="flex shrink-0 gap-1">
                      <IconAction
                        label="Edit entry"
                        icon={<Pencil />}
                        onClick={() =>
                          setEditing({
                            id: faq.id,
                            question: faq.question,
                            answer: faq.answer,
                            audience: faq.audience,
                            sort_order: faq.sort_order,
                            published: faq.published,
                          })
                        }
                      />
                      <IconAction
                        label="Delete entry"
                        icon={<Trash2 />}
                        tone="destructive"
                        onClick={() => setDeleting(faq)}
                      />
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </SheetContent>
      </Sheet>

      <FaqDialog value={editing} onClose={() => setEditing(null)} />
      <ConfirmationDialog
        open={deleting !== null}
        onClose={() => setDeleting(null)}
        onConfirm={confirmDelete}
        title="Delete this entry?"
        message={`"${deleting?.question ?? ""}" will be removed from the Help & support page.`}
        confirmLabel="Delete"
        variant="error"
        loading={deleteFaq.isPending}
      />
    </>
  );
}

function FaqDialog({
  value,
  onClose,
}: {
  value: (typeof EMPTY_FAQ & { id?: number }) | null;
  onClose: () => void;
}) {
  const [form, setForm] = useState(EMPTY_FAQ as typeof EMPTY_FAQ & { id?: number });
  const [touched, setTouched] = useState(false);
  const save = useSaveFaq();

  useEffect(() => {
    if (value) {
      setForm(value);
      setTouched(false);
    }
  }, [value]);

  const questionError = touched && !form.question.trim() ? "Enter the question." : undefined;
  const answerError = touched && !form.answer.trim() ? "Enter the answer." : undefined;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setTouched(true);
    if (!form.question.trim() || !form.answer.trim()) return;
    try {
      await save.mutateAsync(form);
      toast.success(form.id ? "Entry updated" : "Entry added");
      onClose();
    } catch {
      // Shown by the API client.
    }
  };

  return (
    <Dialog open={value !== null} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <form onSubmit={submit} className="space-y-4">
          <DialogHeader>
            <DialogTitle>{form.id ? "Edit entry" : "New entry"}</DialogTitle>
            <DialogDescription>Write the answer the way you'd reply to a ticket.</DialogDescription>
          </DialogHeader>
          <Field label="Question" error={questionError} required>
            <Input
              value={form.question}
              onChange={(e) => setForm({ ...form, question: e.target.value })}
              maxLength={300}
            />
          </Field>
          <Field label="Answer" error={answerError} required>
            <Textarea
              value={form.answer}
              onChange={(e) => setForm({ ...form, answer: e.target.value })}
              rows={6}
              maxLength={5000}
            />
          </Field>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Shown to">
              {({ id }) => (
                <Select
                  value={form.audience}
                  onValueChange={(v) => setForm({ ...form, audience: v as FaqAudience })}
                >
                  <SelectTrigger id={id}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Everyone</SelectItem>
                    <SelectItem value="student">Students</SelectItem>
                    <SelectItem value="partner">Partners</SelectItem>
                  </SelectContent>
                </Select>
              )}
            </Field>
            <Field label="Order" hint="Lower numbers appear first.">
              <Input
                type="number"
                value={form.sort_order}
                onChange={(e) => setForm({ ...form, sort_order: Number(e.target.value) || 0 })}
              />
            </Field>
          </div>
          <label className="flex items-center gap-3 text-sm text-body">
            <Switch
              checked={form.published}
              onCheckedChange={(checked) => setForm({ ...form, published: checked })}
            />
            Published
          </label>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" loading={save.isPending}>
              Save
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
